import { z } from 'zod';
import { EnquiryFormField, EnquiryFormValues } from './types';

/**
 * Validation and defaults built from a form definition at render time.
 *
 * Ported from the RYI widget's form-utility, which is the proven version of this logic. Kept as plain
 * functions with no React or DOM involvement so the rules are unit testable on their own - the component
 * around them cannot be, since this workspace has no React testing library.
 */

/**
 * Option formats, as the RYI widget defines them - it is the only implementation of this data, so it decides
 * what the segments mean:
 *   "Label"                 -> shown and submitted as itself
 *   "Label|value"           -> Label shown, value submitted
 *   "Group|Label|value"     -> Label shown inside an optgroup named Group, value submitted
 * The value is always the last segment; the label is the second segment of three, not the first.
 */
export function optionValue(option: string): string {
  const segments = optionSegments(option);
  return segments[segments.length - 1] ?? '';
}

/** What the customer reads. */
export function optionLabel(option: string): string {
  const segments = optionSegments(option);
  return (segments.length >= 3 ? segments[1] : segments[0]) ?? '';
}

/** The optgroup an option belongs to, or null when it is ungrouped. */
export function optionGroup(option: string): string | null {
  const segments = optionSegments(option);
  return segments.length >= 3 ? segments[0] : null;
}

/**
 * The meaningful segments of an option, empty ones discarded.
 *
 * RYI's dropdown does the same (`opt.split('|').filter(Boolean)`), and it matters: "Label||value" would
 * otherwise count as three segments and be read as a group with a blank label. Dropping the empties makes it
 * the two part option it plainly is.
 */
function optionSegments(option: string): string[] {
  return option
    .split('|')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
}

export interface ParsedOption {
  label: string;
  value: string;
  group: string | null;
}

export function parseOptions(field: EnquiryFormField): ParsedOption[] {
  return (field.fieldOptions ?? [])
    .filter((option) => option.trim().length > 0)
    .map((option) => ({
      label: optionLabel(option),
      value: optionValue(option),
      group: optionGroup(option),
    }));
}

/**
 * The field's pattern as a regular expression, or null when it has none or cannot be compiled.
 *
 * The offers API stores `fieldPattern` without ever compiling it, so a malformed pattern - "[" is enough -
 * reaches the browser intact. Left to `new RegExp` it throws during render and takes the whole enquiry form
 * down, which is a far worse outcome than the one rule it was meant to enforce going unapplied.
 */
function compilePattern(field: EnquiryFormField): RegExp | null {
  if (!field.fieldPattern) return null;

  try {
    return new RegExp(field.fieldPattern);
  } catch {
    console.warn(
      `Enquiry field '${field.fieldName}' has an invalid pattern and it will not be applied: ${field.fieldPattern}`
    );
    return null;
  }
}

/**
 * Options gathered into their optgroups.
 *
 * A three segment option carries a group name ("Group|Label|value"), which the RYI widget renders as an
 * optgroup. Every option of a group joins the same one wherever it appears in the list - RYI buckets by name,
 * and the options editor only appends rows, so an author who returns to a dealer list months later and adds
 * another NSW entry expects it under the NSW heading, not under a second one. Groups appear in the order
 * they were first used, and a list with no groups renders exactly as a flat list of options.
 */
export function groupOptions(
  options: ParsedOption[]
): { group: string | null; options: ParsedOption[] }[] {
  const entries: { group: string | null; options: ParsedOption[] }[] = [];
  const byGroup = new Map<string | null, ParsedOption[]>();

  options.forEach((option) => {
    const existing = byGroup.get(option.group);

    if (existing) {
      existing.push(option);
      return;
    }

    const bucket = [option];
    byGroup.set(option.group, bucket);
    entries.push({ group: option.group, options: bucket });
  });

  return entries;
}

function buildFieldSchema(field: EnquiryFormField): z.ZodTypeAny {
  // A conditionally visible field is optional in the schema whatever its required flag says: whether it
  // applies depends on another answer, so the visibility rule enforces it, not validation. Same decision as
  // the RYI widget.
  const isRequired = field.fieldIsRequired && !field.fieldCondition;
  const label = field.fieldLabel || field.fieldName;

  if (field.fieldType === 'checkbox') {
    const schema = z.boolean();
    return isRequired
      ? schema.refine((value) => value === true, {
          message: `${label} must be checked`,
        })
      : schema.optional();
  }

  if (field.fieldType === 'dropdown') {
    const allowed = parseOptions(field).map((option) => option.value);

    if (allowed.length === 0) {
      // A dropdown with no options is a misconfiguration the API should have rejected. Do not make the form
      // unsubmittable over it - accept anything and let the empty select speak for itself.
      return isRequired
        ? z.string().min(1, { message: `${label} is required` })
        : z.string().optional();
    }

    return z
      .string()
      .refine((value) => (!value && !isRequired) || allowed.includes(value), {
        message: `${label} is required`,
      });
  }

  let schema = z.string();

  if (field.fieldType === 'email') {
    schema = schema.email({ message: 'Please enter a valid email address' });
  }

  if (field.fieldMinLength) {
    schema = schema.min(field.fieldMinLength, {
      message: `${label} must be at least ${field.fieldMinLength} characters`,
    });
  }

  if (field.fieldMaxLength) {
    schema = schema.max(field.fieldMaxLength, {
      message: `${label} must be at most ${field.fieldMaxLength} characters`,
    });
  }

  const pattern = compilePattern(field);
  if (pattern) {
    schema = schema.regex(pattern, {
      message:
        field.fieldPatternCustomMessage ||
        `${label} is not in the expected format`,
    });
  }

  return isRequired
    ? schema.min(1, { message: `${label} is required` })
    : schema.optional().or(z.literal(''));
}

export function buildFormSchema(
  fields: EnquiryFormField[]
): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const shape: Record<string, z.ZodTypeAny> = {};

  fields.forEach((field) => {
    shape[field.fieldName] = buildFieldSchema(field);
  });

  return z.object(shape);
}

export function buildDefaultValues(
  fields: EnquiryFormField[]
): EnquiryFormValues {
  const values: EnquiryFormValues = {};

  fields.forEach((field) => {
    if (field.fieldType === 'checkbox') {
      values[field.fieldName] = false;
      return;
    }

    values[field.fieldName] = defaultTextValue(field);
  });

  return values;
}

/**
 * A dropdown's default has to be one of its own options.
 *
 * Nothing validates that when the form is saved, and a select whose value is not among its options shows the
 * first option while holding the stale one underneath - the customer then fails validation on a field they
 * were never asked to touch. Falling back to empty makes the placeholder show instead, which is honest.
 */
function defaultTextValue(field: EnquiryFormField): string {
  const value = field.fieldDefaultValue ?? '';

  if (field.fieldType !== 'dropdown' || value === '') return value;

  const allowed = parseOptions(field).map((option) => option.value);
  return allowed.includes(value) ? value : '';
}

/**
 * Whether a conditional field should be shown, given the current values. A field with no condition is
 * always visible.
 */
export function isFieldVisible(
  field: EnquiryFormField,
  values: EnquiryFormValues
): boolean {
  if (!field.fieldCondition) return true;

  const watched = values[field.fieldCondition.dependsOn];
  return field.fieldCondition.showWhen.includes(String(watched ?? ''));
}

export function sortFields(fields: EnquiryFormField[]): EnquiryFormField[] {
  return [...fields].sort(
    (left, right) => left.displayOrder - right.displayOrder
  );
}
